import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { RouletteSpeed } from '../../../models/communication/app-state.model';
import { DealerService } from '../../../services/dealer.service';

interface SpeedValues {
    current: number;
    min: number;
    max: number;
}

interface SpeedZoneWidths {
    min: number;
    safe: number;
    current: number;
    max: number;
    status: 'high' | 'low' | 'safe';
}

@Component({
    standalone: true,
    selector: 'app-roulette-velocity-display',
    imports: [CommonModule],
    templateUrl: './velocity-display.component.html',
    styleUrls: ['./velocity-display.component.scss']
})
export class RouletteVelocityDisplayComponent implements OnInit, OnDestroy {
    ballSpeed: SpeedValues = { current: 0, min: 0, max: 0 };
    wheelSpeed: SpeedValues = { current: 0, min: 0, max: 0 };

    ballZoneWidths: SpeedZoneWidths = { min: 0, safe: 0, current: 0, max: 0, status: 'low' };
    wheelZoneWidths: SpeedZoneWidths = { min: 0, safe: 0, current: 0, max: 0, status: 'low' };

    readonly gauges = [
        { label: 'Cylinder velocity', speed: this.wheelSpeed, zone: this.wheelZoneWidths },
        { label: 'Ball velocity', speed: this.ballSpeed, zone: this.ballZoneWidths }
    ];

    private speedSubscription!: Subscription;

    constructor(private readonly dealerService: DealerService) {}

    ngOnInit(): void {
        this.speedSubscription = this.dealerService.getRouletteSpeedObservable().subscribe((event: RouletteSpeed) => {
            this.ballSpeed = event.ballSpeed;
            this.wheelSpeed = event.wheelSpeed;

            this.ballZoneWidths = this.calculateZoneWidths(this.ballSpeed);
            this.wheelZoneWidths = this.calculateZoneWidths(this.wheelSpeed);

            this.gauges[0].speed = this.wheelSpeed;
            this.gauges[0].zone = this.wheelZoneWidths;

            this.gauges[1].speed = this.ballSpeed;
            this.gauges[1].zone = this.ballZoneWidths;
        });
    }

    ngOnDestroy(): void {
        this.speedSubscription.unsubscribe();
    }

    private clampTo(value: number, max: number): number {
        return Math.min(Math.max(0, value), max);
    }

    private calculateZoneWidths(speed: SpeedValues): SpeedZoneWidths {
        const { current, min, max } = speed;

        const gaugeMax = Math.max(1, max + min);
        const multiplier = 100;

        let status: 'high' | 'low' | 'safe';
        if (current < min) {
            status = 'low';
        } else if (current > max) {
            status = 'high';
        } else {
            status = 'safe';
        }

        const minPct = (this.clampTo(min, gaugeMax) / gaugeMax) * multiplier;
        const maxPct = (this.clampTo(max, gaugeMax) / gaugeMax) * multiplier;
        const currentPct = (this.clampTo(current, gaugeMax) / gaugeMax) * multiplier;

        return {
            min: minPct,
            safe: maxPct - minPct,
            current: currentPct,
            max: maxPct,
            status
        };
    }
}
